import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AppHeader from '../../../components/AppHeader.tsx';
import { useAuthentication } from '@/features/auth/useAuthentication';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faCopy, faCut, faEdit, faEuroSign, faEye, faFilter, faPlus, faSearch, faStore, faTags, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useServiceStore } from '../../../features/services/store/useServiceStore.ts';
import { useServiceCategoryStore } from '../../../features/services/store/useServiceCategoryStore.ts';
import { useEmployeeStore } from '../../../features/services/store/useEmployeeStore.ts';
import { useSalonStore } from '../../../features/salons/store/useSalonStore.ts';

const ServicesPricesPage: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const { canManageServices } = useAuthentication();
  const allowManage = canManageServices(entityId);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddService, setShowAddService] = useState(false);
  const [showEditService, setShowEditService] = useState(false);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [newService, setNewService] = useState({
    name: '',
    description: '',
    duration: 60,
    price: 0,
    currency: 'CZK',
    categoryId: '',
    salonId: '',
    color: '#3B82F6', // Přidáme pole pro barvu
    isActive: true,
    isBookable: true,
    requiresDeposit: false,
    depositAmount: 0,
    assignedStaffIds: [] as string[],
    maxAdvanceBookingDays: 30,
    minAdvanceBookingHours: 2,
    cancellationPolicy: '',
  });

  const [editService, setEditService] = useState({
    name: '',
    description: '',
    duration: 60,
    price: 0,
    currency: 'CZK',
    categoryId: '',
    salonId: '',
    color: '#3B82F6', // Přidáme pole pro barvu
    isActive: true,
    isBookable: true,
    requiresDeposit: false,
    depositAmount: 0,
    assignedStaffIds: [] as string[],
    maxAdvanceBookingDays: 30,
    minAdvanceBookingHours: 2,
    cancellationPolicy: '',
  });

  // Stores
  const { services, loading: servicesLoading, error: servicesError, loadServices, deleteService, createService, updateService } = useServiceStore();

  const { categories, loading: categoriesLoading, error: categoriesError, loadCategories } = useServiceCategoryStore();

  const { employees, loading: employeesLoading, error: employeesError, loadEmployees } = useEmployeeStore();

  const { salons, loadSalons } = useSalonStore();

  const breadcrumb = [{ label: 'Nastavení' }, { label: 'Služby a ceny' }];

  // Load data on component mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadServices(), loadCategories(), loadEmployees(), loadSalons()]);
    };
    loadData();
  }, [loadServices, loadCategories, loadEmployees, loadSalons]);

  // Filter services based on search and category
  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) || service.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || service.categoryId === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (categoryId: string) => {
    return (
      categories.find(c => c.id === categoryId) || {
        name: 'Neznámá',
        color: 'bg-gray-100 text-gray-800',
      }
    );
  };

  // Calculate statistics
  const totalServices = services.length;
  const activeServices = services.filter(s => s.isActive).length;
  const averagePrice = services.length > 0 ? Math.round(services.reduce((sum, s) => sum + s.price, 0) / services.length) : 0;

  // Handle service operations
  const handleDeleteService = async (id: string) => {
    if (window.confirm('Opravdu chcete smazat tuto službu?')) {
      try {
        await deleteService(id);
      } catch (error) {
        console.error('Chyba při mazání služby:', error);
      }
    }
  };

  const handleCreateService = async () => {
    try {
      await createService(newService);
      setNewService({
        name: '',
        description: '',
        duration: 60,
        price: 0,
        currency: 'CZK',
        categoryId: '',
        salonId: '',
        color: '#3B82F6',
        isActive: true,
        isBookable: true,
        requiresDeposit: false,
        depositAmount: 0,
        assignedStaffIds: [],
        maxAdvanceBookingDays: 30,
        minAdvanceBookingHours: 2,
        cancellationPolicy: '',
      });
      setShowAddService(false);
    } catch (error) {
      console.error('Chyba při vytváření služby:', error);
    }
  };

  const handleEditService = (service: any) => {
    console.log('🔍 EditService: Načítám službu pro editaci:', service);
    console.log('🔍 EditService: service.color =', service.color);
    
    setEditService({
      name: service.name,
      description: service.description,
      duration: service.duration,
      price: service.price,
      currency: service.currency,
      categoryId: service.categoryId,
      salonId: service.salonId,
      color: service.color || '#3B82F6',
      isActive: service.isActive,
      isBookable: service.isBookable,
      requiresDeposit: service.requiresDeposit,
      depositAmount: service.depositAmount || 0,
      assignedStaffIds: service.assignedStaffIds || [],
      maxAdvanceBookingDays: service.maxAdvanceBookingDays,
      minAdvanceBookingHours: service.minAdvanceBookingHours,
      cancellationPolicy: service.cancellationPolicy || '',
    });
    setEditingService(service.id);
    setShowEditService(true);
  };

  const handleUpdateService = async () => {
    if (!editingService) return;

    try {
      console.log('🔧 Odesílám data pro úpravu služby:', editService);
      console.log('🔧 editService.color =', editService.color);
      await updateService(editingService, editService);
      setShowEditService(false);
      setEditingService(null);
    } catch (error) {
      console.error('Chyba při úpravě služby:', error);
    }
  };

  // Loading state
  const isLoading = servicesLoading || categoriesLoading || employeesLoading;
  const hasError = servicesError || categoriesError || employeesError;

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Služby a ceny" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Služby a ceny</h1>
                <p className="text-gray-600 mt-1">Spravujte nabízené služby a jejich ceny</p>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={() => setShowAddService(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nová služba
                </button>
              </div>
            </div>
          </section>

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faTags} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem služeb</p>
                  <p className="text-2xl font-bold text-gray-900">{totalServices}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faCut} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Aktivní služby</p>
                  <p className="text-2xl font-bold text-gray-900">{activeServices}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrná cena</p>
                  <p className="text-2xl font-bold text-gray-900">{averagePrice} Kč/hod</p>
                </div>
              </div>
            </div>
          </section>

          {/* Search and Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat služby..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                disabled={categoriesLoading}
              >
                <option value="all">Všechny kategorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          {/* Loading State */}
          {isLoading && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-center py-8">
                <div className="text-gray-500">Načítání služeb...</div>
              </div>
            </section>
          )}

          {/* Error State */}
          {hasError && (
            <section className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="text-red-600">
                <p className="font-medium">Chyba při načítání dat:</p>
                <p className="text-sm mt-1">{servicesError || categoriesError || employeesError}</p>
              </div>
            </section>
          )}

          {/* Services Grid */}
          {!isLoading && !hasError && (
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map(service => {
                const categoryInfo = getCategoryInfo(service.categoryId);

                // Per-service staff assignments are no longer exposed by the API in single-salon mode.
                const assignedStaff: typeof employees = [];

                return (
                  <div key={service.id} className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h3 className="font-semibold text-gray-900">{service.name}</h3>
                            <span className={`px-2 py-1 text-xs rounded-full ${categoryInfo.color}`}>{categoryInfo.name}</span>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                        </div>
                        <div className="flex items-center space-x-1">
                          <button className="text-gray-400 hover:text-gray-600 p-1" title="Zobrazit detail">
                            <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleEditService(service)} className="text-gray-400 hover:text-blue-600 p-1" title="Upravit">
                            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteService(service.id)} className="text-gray-400 hover:text-red-600 p-1" title="Smazat">
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Service Details */}
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Doba trvání</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{service.duration} min</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faEuroSign} className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Cena</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {service.price} {service.currency}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faCut} className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Zaměstnanci</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{assignedStaff.length}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faTags} className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Stav</span>
                          </div>
                          <span className={`text-sm font-medium ${service.isActive ? 'text-green-600' : 'text-red-600'}`}>
                            {service.isActive ? 'Aktivní' : 'Neaktivní'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faStore} className="w-4 h-4 mr-2 text-gray-400" />
                            <span className="text-sm text-gray-600">Salon</span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{service.salonName}</span>
                        </div>
                      </div>

                      {/* Staff List */}
                      {assignedStaff.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="text-xs text-gray-500 mb-2">Dostupní zaměstnanci:</div>
                          <div className="flex flex-wrap gap-1">
                            {assignedStaff.map(staff => (
                              <span key={staff.id} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                                {`${staff.firstName ?? ''} ${staff.lastName ?? ''}`.trim()}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          )}

          {/* No Services Message */}
          {!isLoading && !hasError && filteredServices.length === 0 && (
            <section className="bg-white rounded-lg shadow-sm p-6">
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faTags} className="text-4xl text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné služby</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm || filterCategory !== 'all'
                    ? 'Nebyly nalezeny žádné služby odpovídající filtru.'
                    : 'Zatím nemáte žádné služby. Vytvořte první službu kliknutím na tlačítko "Nová služba".'}
                </p>
                {!searchTerm && filterCategory === 'all' && (
                  <button onClick={() => setShowAddService(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Vytvořit první službu
                  </button>
                )}
              </div>
            </section>
          )}

          {/* Quick Actions */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rychlé akce</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setShowAddService(true)}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Nová služba</span>
              </button>
              <button
                onClick={() => {
                  const selectedServices = filteredServices.filter(s => s.id);
                  if (selectedServices.length > 0) {
                    // Implement bulk operations
                    console.log('Hromadné úpravy pro:', selectedServices);
                  }
                }}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <FontAwesomeIcon icon={faCopy} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Hromadné úpravy</span>
              </button>
              <button
                onClick={() => {
                  // Navigate to categories management
                  console.log('Správa kategorií');
                }}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
              >
                <FontAwesomeIcon icon={faTags} className="w-6 h-6 text-gray-400 mr-3" />
                <span className="text-gray-600">Správa kategorií</span>
              </button>
            </div>
          </section>

          {/* Add Service Modal */}
          {showAddService && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Nová služba</h3>
                  <button onClick={() => setShowAddService(false)} className="text-gray-400 hover:text-gray-600">
                    <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Název služby *</label>
                      <input
                        type="text"
                        value={newService.name}
                        onChange={e => setNewService(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Např. Stříhání vlasů"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie *</label>
                      <select
                        value={newService.categoryId}
                        onChange={e => setNewService(prev => ({ ...prev, categoryId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Vyberte kategorii</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
                    <textarea
                      value={newService.description}
                      onChange={e => setNewService(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={3}
                      placeholder="Popis služby..."
                    />
                  </div>

                  {/* Barva služby */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barva služby</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={newService.color}
                        onChange={e => setNewService(prev => ({ ...prev, color: e.target.value }))}
                        className="w-12 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        value={newService.color}
                        onChange={e => setNewService(prev => ({ ...prev, color: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#3B82F6"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Barva se použije pro zobrazení rezervací v kalendáři</p>
                  </div>

                  {/* Salon Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salon *</label>
                    <select
                      value={newService.salonId}
                      onChange={e => setNewService(prev => ({ ...prev, salonId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Vyberte salon</option>
                      {salons.map(salon => (
                        <option key={salon.id} value={salon.id}>
                          {salon.name} - {salon.city}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price and Duration */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Doba trvání (min) *</label>
                      <input
                        type="number"
                        value={newService.duration}
                        onChange={e => setNewService(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cena *</label>
                      <input
                        type="number"
                        value={newService.price}
                        onChange={e => setNewService(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Měna</label>
                      <select
                        value={newService.currency}
                        onChange={e => setNewService(prev => ({ ...prev, currency: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="CZK">CZK</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>

                  {/* Staff Assignment */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Přiřazení zaměstnanců
                        <span className="text-gray-500 ml-1">({newService.assignedStaffIds.length} vybráno)</span>
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setNewService(prev => ({ ...prev, assignedStaffIds: employees.map(emp => emp.id) }))}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Vybrat vše
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewService(prev => ({ ...prev, assignedStaffIds: [] }))}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Zrušit vše
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {employees.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-2">Žádní zaměstnanci nejsou k dispozici</div>
                      ) : (
                        employees.map(employee => (
                          <label key={employee.id} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={newService.assignedStaffIds.includes(employee.id)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setNewService(prev => ({ ...prev, assignedStaffIds: [...prev.assignedStaffIds, employee.id] }));
                                } else {
                                  setNewService(prev => ({ ...prev, assignedStaffIds: prev.assignedStaffIds.filter(id => id !== employee.id) }));
                                }
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim()}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newService.isActive}
                          onChange={e => setNewService(prev => ({ ...prev, isActive: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Aktivní</span>
                      </label>

                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newService.isBookable}
                          onChange={e => setNewService(prev => ({ ...prev, isBookable: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Rezervovatelné</span>
                      </label>

                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={newService.requiresDeposit}
                          onChange={e => setNewService(prev => ({ ...prev, requiresDeposit: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Vyžaduje zálohu</span>
                      </label>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max. dny dopředu</label>
                        <input
                          type="number"
                          value={newService.maxAdvanceBookingDays}
                          onChange={e => setNewService(prev => ({ ...prev, maxAdvanceBookingDays: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          min="1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min. hodiny dopředu</label>
                        <input
                          type="number"
                          value={newService.minAdvanceBookingHours}
                          onChange={e => setNewService(prev => ({ ...prev, minAdvanceBookingHours: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button onClick={() => setShowAddService(false)} className="px-4 py-2 text-gray-600 hover:text-gray-800">
                    Zrušit
                  </button>
                  <button
                    onClick={handleCreateService}
                    disabled={!newService.name || !newService.categoryId || !newService.salonId}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Vytvořit službu
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Service Modal */}
          {showEditService && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900">Upravit službu</h3>
                  <button
                    onClick={() => {
                      setShowEditService(false);
                      setEditingService(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Název služby *</label>
                      <input
                        type="text"
                        value={editService.name}
                        onChange={e => setEditService(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Např. Stříhání vlasů"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Kategorie *</label>
                      <select
                        value={editService.categoryId}
                        onChange={e => setEditService(prev => ({ ...prev, categoryId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="">Vyberte kategorii</option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Popis</label>
                    <textarea
                      value={editService.description}
                      onChange={e => setEditService(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      rows={3}
                      placeholder="Popis služby..."
                    />
                  </div>

                  {/* Barva služby */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Barva služby</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={editService.color}
                        onChange={e => setEditService(prev => ({ ...prev, color: e.target.value }))}
                        className="w-12 h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="text"
                        value={editService.color}
                        onChange={e => setEditService(prev => ({ ...prev, color: e.target.value }))}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="#3B82F6"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Barva se použije pro zobrazení rezervací v kalendáři</p>
                  </div>

                  {/* Salon Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salon *</label>
                    <select
                      value={editService.salonId}
                      onChange={e => setEditService(prev => ({ ...prev, salonId: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <option value="">Vyberte salon</option>
                      {salons.map(salon => (
                        <option key={salon.id} value={salon.id}>
                          {salon.name} - {salon.city}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Price and Duration */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Doba trvání (min) *</label>
                      <input
                        type="number"
                        value={editService.duration}
                        onChange={e => setEditService(prev => ({ ...prev, duration: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cena *</label>
                      <input
                        type="number"
                        value={editService.price}
                        onChange={e => setEditService(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Měna</label>
                      <select
                        value={editService.currency}
                        onChange={e => setEditService(prev => ({ ...prev, currency: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        <option value="CZK">CZK</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>
                  </div>

                  {/* Staff Assignment */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Přiřazení zaměstnanců
                        <span className="text-gray-500 ml-1">({editService.assignedStaffIds.length} vybráno)</span>
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setEditService(prev => ({ ...prev, assignedStaffIds: employees.map(emp => emp.id) }))}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Vybrat vše
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditService(prev => ({ ...prev, assignedStaffIds: [] }))}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Zrušit vše
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                      {employees.length === 0 ? (
                        <div className="text-sm text-gray-500 text-center py-2">Žádní zaměstnanci nejsou k dispozici</div>
                      ) : (
                        employees.map(employee => (
                          <label key={employee.id} className="flex items-center space-x-2 hover:bg-gray-50 p-1 rounded">
                            <input
                              type="checkbox"
                              checked={editService.assignedStaffIds.includes(employee.id)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setEditService(prev => ({ ...prev, assignedStaffIds: [...prev.assignedStaffIds, employee.id] }));
                                } else {
                                  setEditService(prev => ({ ...prev, assignedStaffIds: prev.assignedStaffIds.filter(id => id !== employee.id) }));
                                }
                              }}
                              className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">{`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim()}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editService.isActive}
                          onChange={e => setEditService(prev => ({ ...prev, isActive: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Aktivní</span>
                      </label>

                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editService.isBookable}
                          onChange={e => setEditService(prev => ({ ...prev, isBookable: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Rezervovatelné</span>
                      </label>

                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editService.requiresDeposit}
                          onChange={e => setEditService(prev => ({ ...prev, requiresDeposit: e.target.checked }))}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700">Vyžaduje zálohu</span>
                      </label>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max. dny dopředu</label>
                        <input
                          type="number"
                          value={editService.maxAdvanceBookingDays}
                          onChange={e => setEditService(prev => ({ ...prev, maxAdvanceBookingDays: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          min="1"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Min. hodiny dopředu</label>
                        <input
                          type="number"
                          value={editService.minAdvanceBookingHours}
                          onChange={e => setEditService(prev => ({ ...prev, minAdvanceBookingHours: parseInt(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowEditService(false);
                      setEditingService(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Zrušit
                  </button>
                  <button
                    onClick={handleUpdateService}
                    disabled={!editService.name || !editService.categoryId || !editService.salonId}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Uložit změny
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesPricesPage;
